import { computed, ref, watch } from 'vue'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'
import { isSystemSlot, normalizePageOrders } from '../utils/pages'
import { useBaseState } from './useBaseState'

import type { PageItem } from '../utils/types'

/** 获取当前底座设备的屏显 feature。 */
function getBaseScreen() {
  const deviceStore = useDeviceStore()
  return deviceStore.currentDevice?.features.baseScreen
}

/** 切换页面显示状态的结果。 */
export type TogglePageDisplayResult = 'toggled' | 'not-found' | 'min-one' | 'system-locked'

/**
 * 根据 pending 显示状态构建写入设备的排序参数。
 * 显示中的页面在前，有 id 的隐藏页面次之，空槽位最后。
 */
function buildDisplayOrder(pages: PageItem[], showBySlot: Record<number, boolean>) {
  const isShown = (page: PageItem) => page.kind !== 'custom' || (showBySlot[page.slot] ?? page.show)

  const shown = pages.filter((page) => isShown(page))
  const hiddenWithId = pages.filter((page) => !isShown(page) && page.id)
  const rest = pages.filter((page) => !isShown(page) && !page.id)

  return {
    imageNum: shown.length,
    order: [...shown, ...hiddenWithId, ...rest].map((page) => page.slot),
  }
}

/** 播放设置（轮播显示/隐藏、停留时长等）。 */
export function usePlaybackSetting() {
  const { pages, pageConfig, activePage, loading, syncPages, refreshPages } = useBaseState()

  /** 已提交到设备的各槽位显示状态快照。 */
  const committedShowBySlot = ref<Record<number, boolean>>({})
  /** 工具栏中待保存的各槽位显示状态。 */
  const pendingShowBySlot = ref<Record<number, boolean>>({})
  /** 待保存的显示中页面槽位顺序；null 表示与当前 pages 顺序一致。 */
  const pendingSortSlots = ref<number[] | null>(null)

  const isPageShown = (page: PageItem, showBySlot = pendingShowBySlot.value) =>
    page.kind !== 'custom' || (showBySlot[page.slot] ?? page.show)

  /** 获取当前生效的显示中页面槽位顺序。 */
  const getEffectiveShownSlots = () => {
    const shownSlots = new Set(
      pages.value.filter((page) => isPageShown(page)).map((page) => page.slot),
    )
    const systemSlots = pageConfig.value.systemPages
      .map((page) => page.slot)
      .filter((slot) => shownSlots.has(slot))
    const customSlots = pages.value
      .filter((page) => page.kind === 'custom' && shownSlots.has(page.slot))
      .map((page) => page.slot)

    return [...systemSlots, ...customSlots]
  }

  /** 从 pages 同步 committed / pending 显示快照，并重置排序 pending。 */
  const syncShowSnapshots = () => {
    const next: Record<number, boolean> = {}
    pages.value.forEach((page) => {
      next[page.slot] = page.kind !== 'custom' || page.show
    })
    committedShowBySlot.value = next
    pendingShowBySlot.value = { ...next }
    pendingSortSlots.value = null
  }

  watch(
    pages,
    () => {
      syncShowSnapshots()
    },
    { deep: true, immediate: true },
  )

  void refreshPages()

  /** 当前轮播中实际显示的页面列表。 */
  const carouselItems = computed(() => pages.value.filter((page) => page.show))

  /** 工具栏展示的页面列表（所有系统页面 + 已上传页面，含 pending 显示状态）。 */
  const toolbarPages = computed(() =>
    pages.value
      .filter((page) => page.kind !== 'custom' || page.id)
      .map((page) => ({
        ...page,
        show: pendingShowBySlot.value[page.slot] ?? page.show,
      })),
  )

  /** pending 显示状态是否与已提交状态存在差异。 */
  const hasDisplayChanges = computed(() =>
    pages.value.some(
      (page) =>
        (pendingShowBySlot.value[page.slot] ?? page.show) !== committedShowBySlot.value[page.slot],
    ),
  )

  /** 播放顺序 tab 展示的页面列表（仅显示中的页面，含 pending 排序）。 */
  const sortPages = computed(() => {
    const slotOrder = pendingSortSlots.value ?? getEffectiveShownSlots()

    return slotOrder
      .map((slot, index) => {
        const page = pages.value.find((item) => item.slot === slot)
        return page ? { ...page, order: index + 1 } : null
      })
      .filter((page): page is PageItem => page !== null)
  })

  /** pending 播放顺序是否与当前生效顺序存在差异。 */
  const hasSortChanges = computed(() => {
    if (!pendingSortSlots.value) return false

    const effectiveSlots = getEffectiveShownSlots()
    if (pendingSortSlots.value.length !== effectiveSlots.length) return true

    return pendingSortSlots.value.some((slot, index) => slot !== effectiveSlots[index])
  })

  /** 显示或排序存在未上传的改动。 */
  const hasPendingChanges = computed(() => hasDisplayChanges.value || hasSortChanges.value)

  /** 将 pending 排序应用到 pages 数组（显示项在前，隐藏项在后）。 */
  const applyPendingSortToPages = () => {
    if (!pendingSortSlots.value) return

    const shown = pendingSortSlots.value
      .map((slot) => pages.value.find((page) => page.slot === slot))
      .filter((page): page is PageItem => page !== null)
    const hiddenWithId = pages.value.filter((page) => !isPageShown(page) && page.id)
    const rest = pages.value.filter((page) => !isPageShown(page) && !page.id)

    pages.value = normalizePageOrders([...shown, ...hiddenWithId, ...rest])
  }

  /** 更新待保存的播放顺序（拖动排序后调用）。 */
  const reorderSortPages = (orderedPages: PageItem[]) => {
    const shownSlots = new Set(orderedPages.map((page) => page.slot))
    const systemSlots = pageConfig.value.systemPages
      .map((page) => page.slot)
      .filter((slot) => shownSlots.has(slot))
    const customSlots = orderedPages
      .filter((page) => page.kind === 'custom')
      .map((page) => page.slot)

    pendingSortSlots.value = [...systemSlots, ...customSlots]
  }

  /** 设置指定槽位的停留时长并写入设备；失败时回滚本地值。 */
  const setPageDelay = async (slot: number, delay: number) => {
    const pageIndex = pages.value.findIndex((item) => item.slot === slot)
    if (pageIndex === -1) return

    const baseScreen = getBaseScreen()
    if (!baseScreen) return

    const previousDelay = pages.value[pageIndex].delay
    if (previousDelay === delay) return

    pages.value[pageIndex].delay = delay

    try {
      await baseScreen.setImageTime(slot, delay)
    } catch (err) {
      if (pages.value[pageIndex]?.delay === delay) {
        pages.value[pageIndex].delay = previousDelay
      }
      console.error('Failed to set page delay')
      defaultReportError(`Failed to set page delay: ${JSON.stringify(err)}`)
    }
  }

  /**
   * 切换指定槽位的显示/隐藏（仅修改 pending，不立即写入设备）。
   * @returns toggled 切换成功；not-found 槽位不存在；min-one 至少保留一页显示；system-locked 系统页不可隐藏
   */
  const togglePageDisplay = (slot: number): TogglePageDisplayResult => {
    const page = pages.value.find((item) => item.slot === slot)
    if (!page) return 'not-found'
    if (isSystemSlot(pageConfig.value, slot)) return 'system-locked'

    const pendingShow = pendingShowBySlot.value[slot] ?? page.show

    if (pendingShow) {
      const pendingShownCount = pages.value.filter(
        (item) => pendingShowBySlot.value[item.slot] ?? item.show,
      ).length
      if (pendingShownCount <= 1) return 'min-one'
    }

    pendingShowBySlot.value = {
      ...pendingShowBySlot.value,
      [slot]: !pendingShow,
    }
    pendingSortSlots.value = null

    return 'toggled'
  }

  /** 将 pending 显示/排序写入设备，并同步本地 pages 与快照；失败时回滚。 */
  const setPageDisplay = async () => {
    const baseScreen = getBaseScreen()
    if (!baseScreen || !hasPendingChanges.value) return

    const previousPendingBySlot = { ...pendingShowBySlot.value }
    const previousPages = pages.value.map((page) => ({ ...page }))
    const previousPendingSort = pendingSortSlots.value ? [...pendingSortSlots.value] : null

    if (hasSortChanges.value) {
      applyPendingSortToPages()
    }

    const { imageNum, order } = buildDisplayOrder(pages.value, pendingShowBySlot.value)

    try {
      loading.value = true
      await baseScreen.setImageOrder(imageNum, order)
      await syncPages()
      syncShowSnapshots()

      if (!pages.value.find((item) => item.slot === activePage.value)?.show) {
        const nextActive = pages.value.find((item) => item.show)
        if (nextActive) activePage.value = nextActive.slot
      }
    } catch (err) {
      pendingShowBySlot.value = previousPendingBySlot
      pendingSortSlots.value = previousPendingSort
      pages.value = previousPages
      console.error('Failed to set page display')
      defaultReportError(`Failed to set page display: ${JSON.stringify(err)}`)
    } finally {
      loading.value = false
    }
  }

  return {
    activePage,
    carouselItems,
    toolbarPages,
    sortPages,
    hasDisplayChanges,
    hasSortChanges,
    hasPendingChanges,
    loading,
    togglePageDisplay,
    setPageDelay,
    reorderSortPages,
    setPageDisplay,
  }
}
